<template>
  <section class="duplicate-device">
    <b-card no-body class="mb-2">
      <b-card-body>
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-1 duplicate-header">
          <div>
            <h4 class="mb-0">Duplicate Device</h4>
            <small class="text-muted">Users sharing the same device token</small>
          </div>
          <div class="d-flex flex-wrap align-items-center duplicate-actions">
            <b-form-input
              v-model="searchTerm"
              size="sm"
              class="duplicate-search"
              placeholder="Search username..."
            />
            <b-form-select
              v-model="minGroupSize"
              :options="minGroupOptions"
              size="sm"
              class="duplicate-filter"
            />
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="isLoading"
              @click="getItems"
            >
              Refresh
            </b-button>
          </div>
        </div>

        <b-row class="mb-1">
          <b-col
            v-for="card in summaryCards"
            :key="card.label"
            xl="3"
            sm="6"
            class="mb-1"
          >
            <div class="d-flex align-items-center duplicate-summary-card">
              <b-avatar size="44" :variant="card.color" class="mr-1">
                <feather-icon :icon="card.icon" size="18" />
              </b-avatar>
              <div>
                <div class="font-weight-bolder">{{ card.value }}</div>
                <small class="text-muted">{{ card.label }}</small>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-card-body>
        <div v-if="isLoading" class="duplicate-skeleton">
          <div v-for="n in 6" :key="`duplicate-skeleton-${n}`" class="duplicate-skeleton-card">
            <div class="duplicate-skeleton-line duplicate-skeleton-line--lg"></div>
            <div class="duplicate-skeleton-line"></div>
            <div class="duplicate-skeleton-line"></div>
          </div>
        </div>
        <b-alert v-else-if="loadError" variant="danger" show>
          {{ loadError }}
        </b-alert>
        <div v-else-if="filteredGroups.length === 0" class="text-center text-muted my-2">
          No duplicate users found. Try adjusting the search or group size filter.
        </div>
        <div v-else class="duplicate-group-list">
          <div
            v-for="(group, index) in filteredGroups"
            :key="`dup-group-${index}`"
            class="duplicate-group-row"
          >
            <div class="duplicate-group-meta">
              <div class="font-weight-bolder">Group #{{ index + 1 }}</div>
              <small class="text-muted">{{ group.length }} users</small>
            </div>
            <div class="duplicate-group-content">
              <small
                class="duplicate-group-status"
                :class="statusClass(group)"
              >
                {{ resolveGroupStatus(group) }}
              </small>
              <div class="duplicate-group-chips">
                <b-badge
                  v-for="(user, userIndex) in visibleUsers(group, index)"
                  :key="user.id || user.user_id || user.user_name || userIndex"
                  variant="light-warning"
                  class="duplicate-chip"
                >
                  {{ resolveUserName(user) }}
                </b-badge>
                <b-badge
                  v-if="!isGroupExpanded(index) && group.length > maxChips"
                  variant="light-secondary"
                  class="duplicate-chip duplicate-chip--action"
                  role="button"
                  tabindex="0"
                  :title="`Show all ${group.length} users`"
                  @click="toggleGroup(index)"
                  @keyup.enter="toggleGroup(index)"
                >
                  +{{ group.length - maxChips }} more
                </b-badge>
                <b-badge
                  v-else-if="isGroupExpanded(index) && group.length > maxChips"
                  variant="light-secondary"
                  class="duplicate-chip duplicate-chip--action"
                  role="button"
                  tabindex="0"
                  title="Show less"
                  @click="toggleGroup(index)"
                  @keyup.enter="toggleGroup(index)"
                >
                  Show less
                </b-badge>
              </div>
            </div>
            <b-badge variant="light-warning" class="duplicate-flag">Duplicate</b-badge>
          </div>
        </div>
      </b-card-body>
    </b-card>
  </section>
</template>

<script>
import {
  BAlert,
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCol,
  BFormInput,
  BFormSelect,
  BRow,
} from "bootstrap-vue";
import { DUPLICATE_DEVICES } from "@core/services/api";

export default {
  components: {
    BAlert,
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormInput,
    BFormSelect,
    BRow,
  },
  data() {
    return {
      ispUser: JSON.parse(localStorage.getItem("userData")) || {},
      items: [],
      searchTerm: "",
      minGroupSize: 2,
      minGroupOptions: [
        { value: 2, text: "2+ Users" },
        { value: 3, text: "3+ Users" },
        { value: 4, text: "4+ Users" },
        { value: 5, text: "5+ Users" },
      ],
      maxChips: 4,
      expandedGroups: {},
      isLoading: false,
      loadError: "",
    };
  },
  computed: {
    normalizedGroups() {
      return (this.items || []).map((group) => {
        if (Array.isArray(group)) return group;
        if (!group) return [];
        return Object.values(group);
      });
    },
    filteredGroups() {
      const term = this.searchTerm.trim().toLowerCase();
      const minSize = Number(this.minGroupSize) || 2;
      return this.normalizedGroups.filter((group) => {
        if (group.length < minSize) return false;
        if (!term) return true;
        return group.some((user) => this.resolveUserName(user).toLowerCase().includes(term));
      });
    },
    summaryCards() {
      const totalGroups = this.normalizedGroups.length;
      const visibleGroups = this.filteredGroups.length;
      const affectedUsers = this.filteredGroups.reduce((sum, group) => sum + group.length, 0);
      const largestGroup = this.filteredGroups.reduce((max, group) => Math.max(max, group.length), 0);

      return [
        { label: "Total Groups", value: totalGroups, icon: "LayersIcon", color: "light-primary" },
        { label: "Visible Groups", value: visibleGroups, icon: "FilterIcon", color: "light-warning" },
        { label: "Affected Users", value: affectedUsers, icon: "UsersIcon", color: "light-success" },
        { label: "Largest Group", value: largestGroup, icon: "TrendingUpIcon", color: "light-info" },
      ];
    },
  },
  mounted() {
    this.getItems();
  },
  methods: {
    resolveUserName(user) {
      return user?.user_name || user?.username || user?.title || "Unknown";
    },
    resolveGroupStatus(group) {
      if (!Array.isArray(group) || group.length === 0) {
        return "No FCM Token Detected - Login to get live FCM Token";
      }
      const tokens = group
        .map((user) => {
          if (!user || typeof user !== "object") return null;
          if (user.users && user.users.fcm_token) return user.users.fcm_token;
          if (user.fcm_token) return user.fcm_token;
          return null;
        })
        .filter((token) => token);

      if (tokens.length === 0) {
        return "No FCM Token Detected - Login to get live FCM Token";
      }
      return "Same Device FCM Token";
    },
    statusClass(group) {
      return this.resolveGroupStatus(group).startsWith("No FCM Token")
        ? "duplicate-group-status--warn"
        : "duplicate-group-status--ok";
    },
    visibleUsers(group, index) {
      if (!Array.isArray(group)) return [];
      return this.isGroupExpanded(index) ? group : group.slice(0, this.maxChips);
    },
    isGroupExpanded(index) {
      return Boolean(this.expandedGroups[index]);
    },
    toggleGroup(index) {
      this.$set(this.expandedGroups, index, !this.expandedGroups[index]);
    },
    getItems() {
      if (!this.ispUser?.id && !this.ispUser?.isp_id) {
        this.loadError = "Unable to detect ISP.";
        return;
      }
      this.isLoading = true;
      this.loadError = "";
      this.$http
        .get(process.env.VUE_APP_BASEURL + DUPLICATE_DEVICES, {
          params: {
            isp_id: this.ispUser.id || this.ispUser.isp_id,
          },
        })
        .then((res) => {
          const payload = res.data && res.data.data ? res.data.data : {};
          this.items = Array.isArray(payload.items) ? payload.items : [];
        })
        .catch(() => {
          this.loadError = "Failed to load duplicate device data.";
          this.items = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss">
.duplicate-device {
  .duplicate-actions {
    gap: 0.5rem;
  }

  .duplicate-search {
    width: 200px;
    flex: 0 0 200px;
  }

  .duplicate-filter {
    width: 120px;
    flex: 0 0 120px;
  }

  .duplicate-summary-card {
    background: #f8fafc;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 0.6rem;
    padding: 0.6rem;
  }

  .duplicate-chip {
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    color: #475569;
    margin: 0 0.35rem 0.35rem 0;
    font-size: 0.75rem;
    padding: 0.2rem 0.45rem;
  }

  .duplicate-chip--action {
    cursor: pointer;
    background: #e2e8f0;
    color: #334155;
  }

  .duplicate-chip--action:hover {
    background: #cbd5e1;
  }
}

.duplicate-group-list {
  display: grid;
  gap: 0.5rem;
}

.duplicate-group-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid #e6e8eb;
  border-radius: 0.65rem;
  background: #fff;
}

.duplicate-group-meta {
  min-width: 120px;
}

.duplicate-group-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.duplicate-group-status {
  font-size: 0.75rem;
  color: #8b93a6;
}

.duplicate-group-status--warn {
  color: #b45309;
}

.duplicate-group-status--ok {
  color: #0f766e;
}

.duplicate-group-row {
  background: #fff;
}

.duplicate-group-row:hover {
  background: #f8fafc;
}

.duplicate-flag {
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #be123c;
}

.duplicate-group-chips {
  display: flex;
  flex-wrap: wrap;
}

.duplicate-flag {
  align-self: flex-start;
}

@media (max-width: 768px) {
  .duplicate-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .duplicate-search,
  .duplicate-filter {
    width: 100%;
    flex: 1 0 100%;
  }

  .duplicate-group-row {
    flex-direction: column;
  }

  .duplicate-group-meta {
    min-width: auto;
  }
}

.duplicate-skeleton {
  display: grid;
  gap: 0.75rem;
}

.duplicate-skeleton-card {
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 0.6rem;
  padding: 0.75rem;
  display: grid;
  gap: 0.5rem;
}

.duplicate-skeleton-line {
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(90deg, #eceff3 0%, #f7f8fa 50%, #eceff3 100%);
  background-size: 200% 100%;
  animation: duplicate-skeleton 1.4s ease-in-out infinite;
}

.duplicate-skeleton-line--lg {
  height: 12px;
  max-width: 200px;
}

@keyframes duplicate-skeleton {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
